import { useHotkey } from '@app/core/hotkeys/useHotkey';
import { hoursToMs, minsToMs } from '@mi-front/core';
import { useOpenFragmentDrawer } from '../../components/focus/selection/useOpenFragmentDrawer';
import { useDownloadMediaFromSelection } from '../../features/download-media';
import { useAddChunkFromSelection } from '../../fragment-editor/hooks/useAddChunkFromSelection';
import { useStreamSelection } from '../../stores/useStreamSelection';
import { streamHotkeys } from '../../stream-hotkeys';
import {
  useFullZoomInFocus,
  useFullZoomOutFocus,
  useMoveFocus,
  useZoomInFocus,
  useZoomInSelectionEndFocus,
  useZoomInSelectionStartFocus,
  useZoomOutFocus,
} from '../focus/useFocusActions';
import { useMoveStream } from '../navigation/useMoveStream';
import {
  useSetSelectionEndFromCursor,
  useSetSelectionStartFromCursor,
} from '../selection/useSelectionActions';

/**
 * Core stream hotkeys that don't require PlaybackProvider context.
 * These hotkeys handle focus navigation, zoom, selection from cursor, and fragment actions.
 *
 * Playback-dependent hotkeys (play/pause, cursor movement, selection from timestamp)
 * are handled separately in useStreamPlaybackHotkeys.
 */
export function useStreamHotkeysCore() {
  const moveStream = useMoveStream();
  const openFragmentDrawer = useOpenFragmentDrawer();
  const { clearSelection, incrementSelection, decrementSelection } = useStreamSelection();
  const moveFocus = useMoveFocus();
  const fullZoomInFocus = useFullZoomInFocus();
  const fullZoomOutFocus = useFullZoomOutFocus();
  const zoomInFocus = useZoomInFocus();
  const zoomOutFocus = useZoomOutFocus();
  const zoomInSelectionStartFocus = useZoomInSelectionStartFocus();
  const zoomInSelectionEndFocus = useZoomInSelectionEndFocus();
  const { downloadMedia } = useDownloadMediaFromSelection();
  const setSelectionStartFromCursor = useSetSelectionStartFromCursor();
  const setSelectionEndFromCursor = useSetSelectionEndFromCursor();
  const { addChunkFromSelection } = useAddChunkFromSelection();

  // Focus navigation
  useHotkey(streamHotkeys.focus.hotkeys.FOCUS_BACK_5M, () => {
    moveFocus(minsToMs(-5));
  });

  useHotkey(streamHotkeys.focus.hotkeys.STREAM_BACK_1H, () => {
    moveStream(hoursToMs(-1));
  });

  useHotkey(streamHotkeys.focus.hotkeys.FOCUS_FORWARD_5M, () => {
    moveFocus(minsToMs(5));
  });

  useHotkey(streamHotkeys.focus.hotkeys.STREAM_FORWARD_1H, () => {
    moveStream(hoursToMs(1));
  });

  // Zoom
  useHotkey(streamHotkeys.focus.hotkeys.FOCUS_FULL_IN, () => {
    fullZoomInFocus();
  });

  useHotkey(streamHotkeys.focus.hotkeys.FOCUS_FULL_OUT, () => {
    fullZoomOutFocus();
  });

  useHotkey(streamHotkeys.focus.hotkeys.FOCUS_ZOOM_SELECTION_IN, () => {
    zoomInSelectionStartFocus();
  });

  useHotkey(streamHotkeys.focus.hotkeys.FOCUS_ZOOM_SELECTION_OUT, () => {
    zoomInSelectionEndFocus();
  });

  useHotkey(streamHotkeys.focus.hotkeys.ZOOM_IN_FOCUS, () => {
    zoomInFocus(minsToMs(0.5));
  });

  useHotkey(streamHotkeys.focus.hotkeys.ZOOM_OUT_FOCUS, () => {
    zoomOutFocus(minsToMs(0.5));
  });

  // Selection from cursor
  useHotkey(streamHotkeys.selection.hotkeys.START_CURSOR_SELECTION, () => {
    setSelectionStartFromCursor();
  });

  useHotkey(streamHotkeys.selection.hotkeys.END_CURSOR_SELECTION, () => {
    setSelectionEndFromCursor();
  });

  useHotkey(streamHotkeys.selection.hotkeys.INCREMENT_CURSOR_SELECTION, () => {
    incrementSelection(5000);
  });

  useHotkey(streamHotkeys.selection.hotkeys.DECREMENT_CURSOR_SELECTION, () => {
    decrementSelection(5000);
  });

  useHotkey(streamHotkeys.selection.hotkeys.CLEAR_SELECTION, () => {
    clearSelection();
  });

  // Fragment actions
  useHotkey(streamHotkeys.selection.hotkeys.ADD_CHUNK_FROM_FOCUS_SELECTION, () => {
    addChunkFromSelection();
  });

  useHotkey(streamHotkeys.selection.hotkeys.CREATE_FRAGMENT, () => {
    openFragmentDrawer();
  });

  useHotkey(streamHotkeys.selection.hotkeys.DOWNLOAD_SELECTION, () => {
    downloadMedia();
  });
}
